// The replay player (plan step 3.3; spec 10.1, 10.2): a session's wireframe at any moment, with
// play, speed, keyframe steps and a scrubber, the session's events on the same axis and the
// gaps marked. Segment content is the customer's data: it is drawn with React elements only,
// as SVG shapes and text nodes, with colors and sizes from numbers (lib/replay.ts checks them),
// and nothing in a segment is ever loaded as a URL. Segments come through the read API, a few at
// a time; one that is missing, unreadable or invalid is a gap, never an error.
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Component,
  type KeyboardEvent,
  type ReactNode,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react'
import { fetchAttachment, replaySegmentsQuery } from '../api/queries'
import type { ReplaySegment } from '../api/types'
import { type MessageId, type Translator, useI18n } from '../i18n'
import { duration, offset } from '../lib/format'
import {
  adjacentKeyframe,
  type Box,
  BREAK_SHARE,
  BREAKS,
  boxes,
  buildTimeline,
  type GapReason,
  keyframeNear,
  LIMITS,
  maskedRuns,
  momentAt,
  parseSegment,
  rgba,
  type Screen,
  type SegmentSource,
  type Timeline,
  trackScale,
} from '../lib/replay'
import { parseTime } from '../lib/session'
import { Icon } from './icons'

/** Segments fetched at once. */
const CONCURRENCY = 4
const SPEEDS = [0.5, 1, 2, 4] as const
const speedText = (v: number) => (v === 0.5 ? '½×' : `${v}×`)
const STEP_MS = 5_000
/** The scrubber's resolution: positions on the track, not milliseconds (breaks are compressed). */
const TRACK = 10_000
/** A break on the track, in pixels (Console D): about 6% of it, never under 24 or over 48. */
const BREAK_PX = { min: 24, max: 48 } as const

type Loaded =
  | { status: 'loaded'; frames: NonNullable<SegmentSource['frames']> }
  | { status: 'invalid' | 'error' }

/** The session's segments and their frames, loaded in time order, a few at a time. */
export function useReplay(projectId: string, sessionId: string) {
  const qc = useQueryClient()
  const list = useQuery(replaySegmentsQuery(projectId, sessionId))
  const [loaded, setLoaded] = useState<Record<string, Loaded>>({})
  const segments = list.data
  useEffect(() => {
    if (!segments?.length) return
    let stopped = false
    const queue = segments.filter((s) => s.stored)
    const next = async (): Promise<void> => {
      const s = queue.shift()
      if (!s || stopped) return
      let result: Loaded
      try {
        const raw = await qc.fetchQuery({
          queryKey: ['project', projectId, 'attachment', s.app_id, s.attachment_id],
          queryFn: () => fetchAttachment(projectId, s.attachment_id, s.app_id),
          staleTime: Number.POSITIVE_INFINITY,
          retry: 1,
        })
        const parsed = parseSegment(raw, sessionId)
        result = parsed.ok ? { status: 'loaded', frames: parsed.frames } : { status: 'invalid' }
      } catch {
        result = { status: 'error' }
      }
      if (stopped) return
      setLoaded((m) => ({ ...m, [s.attachment_id]: result }))
      return next()
    }
    for (let i = 0; i < CONCURRENCY; i++) void next()
    return () => {
      stopped = true
    }
  }, [qc, projectId, sessionId, segments])
  // A timeline that cannot be built is no replay; the session page stays (ReplayBoundary too).
  const timeline = useMemo(() => {
    if (!segments?.length) return undefined
    try {
      return buildTimeline(segments.map((s) => source(s, loaded)))
    } catch {
      return null
    }
  }, [segments, loaded])
  const pending = segments ? segments.filter((s) => s.stored && !loaded[s.attachment_id]).length : 0
  return { list, segments: segments ?? [], timeline, pending }
}

function source(s: ReplaySegment, loaded: Record<string, Loaded>): SegmentSource {
  const l = loaded[s.attachment_id]
  return {
    attachmentId: s.attachment_id,
    startAt: parseTime(s.start_at),
    endAt: s.end_at ? parseTime(s.end_at) : null,
    firstSequence: s.first_sequence,
    lastSequence: s.last_sequence,
    status: !s.stored ? 'missing' : (l?.status ?? 'loading'),
    ...(l?.status === 'loaded' ? { frames: l.frames } : {}),
  }
}

const GAP_KEY: Record<GapReason, MessageId> = {
  missing: 'replay.gap.missing',
  invalid: 'replay.gap.invalid',
  error: 'replay.gap.error',
  loading: 'replay.gap.loading',
  sequence: 'replay.gap.sequence',
  'no-keyframe': 'replay.gap.noKeyframe',
  limit: 'replay.gap.limit',
  cut: 'replay.break.cut',
}

/** A replay that fails to draw says so in its panel; the session page around it stays. */
/** What a break on the track says, by why nothing is there. */
const BREAK_KEY: Partial<Record<GapReason, MessageId>> = {
  cut: 'replay.break.cut',
  missing: 'replay.gap.missing',
  invalid: 'replay.gap.invalid',
  error: 'replay.gap.error',
}

export class ReplayBoundary extends Component<
  { fallback: ReactNode; children: ReactNode },
  { failed: boolean }
> {
  override state = { failed: false }

  static getDerivedStateFromError() {
    return { failed: true }
  }

  override render() {
    return this.state.failed ? this.props.fallback : this.props.children
  }
}

export interface ReplayMarker {
  id: string
  /** Milliseconds since the Unix epoch. */
  at: number
  /** A telemetry name: never translated. */
  name: string
  tone: 'crash' | 'warn' | 'neutral'
}

/**
 * The player. The moment is the caller's (`at`, ms since the epoch), so the session's other
 * views can move it; `onSeek` asks for another one.
 */
export function ReplayPlayer({
  timeline,
  sessionStart,
  at,
  onSeek,
  markers,
  pending,
}: {
  timeline: Timeline
  sessionStart: number
  at: number
  onSeek: (at: number) => void
  markers: ReplayMarker[]
  pending: number
}) {
  const i18n = useI18n()
  const { t } = i18n
  const [playing, setPlaying] = useState(false)
  const [speed, setSpeed] = useState<(typeof SPEEDS)[number]>(1)
  const atRef = useRef(at)
  atRef.current = at
  const clamp = (v: number) => Math.min(timeline.endAt, Math.max(timeline.startAt, v))
  // A break's share of the track, from the track's width (24 to 48 px).
  const trackRef = useRef<HTMLDivElement>(null)
  const [share, setShare] = useState(BREAK_SHARE)
  useEffect(() => {
    const el = trackRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const measure = () => {
      const w = el.clientWidth
      if (w > 0) setShare(Math.min(BREAK_PX.max, Math.max(BREAK_PX.min, w * BREAK_SHARE)) / w)
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [])
  /** Nothing is shown inside a break: forward lands after it, back before it. */
  const outside = (v: number, direction: 1 | -1) => {
    const b = timeline.gaps.find((g) => BREAKS.has(g.reason) && v >= g.from && v < g.to)
    if (!b) return v
    return direction === 1 ? b.to : Math.max(timeline.startAt, b.from - 1)
  }

  // Playback: wall time times the speed, frame by frame; stops at the end.
  // biome-ignore lint/correctness/useExhaustiveDependencies: outside reads only the timeline, a dependency
  useEffect(() => {
    if (!playing) return
    let last = performance.now()
    let raf = requestAnimationFrame(function step(now) {
      // Across a break in one step: nothing was recorded there.
      const next = outside(atRef.current + (now - last) * speed, 1)
      last = now
      if (next >= timeline.endAt) {
        onSeek(timeline.endAt)
        setPlaying(false)
        return
      }
      onSeek(next)
      raf = requestAnimationFrame(step)
    })
    return () => cancelAnimationFrame(raf)
  }, [playing, speed, timeline, onSeek])

  const seekKeyframe = (direction: 1 | -1) => {
    const k = adjacentKeyframe(timeline, atRef.current, direction)
    if (k !== undefined) onSeek(k)
  }
  const onKeyDown = (e: KeyboardEvent<HTMLFieldSetElement>) => {
    const target = e.target as HTMLElement
    // On the scrubber, Space is not play: the slider keeps its own keys but the arrows, which
    // step in time here and across breaks.
    const onRange = target.tagName === 'INPUT'
    const keys: Record<string, () => void> = {
      ...(onRange ? {} : { ' ': () => setPlaying((p) => !p), k: () => setPlaying((p) => !p) }),
      ArrowLeft: () => onSeek(outside(clamp(atRef.current - STEP_MS), -1)),
      ArrowRight: () => onSeek(outside(clamp(atRef.current + STEP_MS), 1)),
      Home: () => onSeek(timeline.startAt),
      End: () => onSeek(timeline.endAt),
      PageUp: () => seekKeyframe(-1),
      PageDown: () => seekKeyframe(1),
    }
    const action = keys[e.key]
    if (!action) return
    e.preventDefault()
    action()
  }

  const moment = momentAt(timeline, at)
  const gap = timeline.gaps.find((g) => at >= g.from && at < g.to)
  const position = offset(i18n, at - sessionStart)
  const total = offset(i18n, timeline.endAt - sessionStart)
  const scale = useMemo(() => trackScale(timeline, share), [timeline, share])
  const pct = (v: number) => `${scale.at(v) * 100}%`
  const icon = (name: 'left' | 'right' | 'play' | 'pause') => <Icon name={name} small />
  // The recorded stretches between the breaks, drawn as the track's pieces.
  const pieces: [number, number][] = []
  let from = timeline.startAt
  for (const b of scale.breaks) {
    if (b.from > from) pieces.push([from, b.from])
    from = b.to
  }
  if (timeline.endAt > from || pieces.length === 0) pieces.push([from, timeline.endAt])

  return (
    // The player's keys work while focus is on any of its controls (they bubble here): Space,
    // the arrows, Home, End, Page Up and Page Down. They are listed in the ? dialog.
    <fieldset className="replay rp" aria-label={t('replay.title')} onKeyDown={onKeyDown}>
      <div className="rp-phone">
        {moment.kind === 'screen' ? (
          <Wireframe screen={moment.screen} label={t('replay.figure', { at: position })} />
        ) : (
          <div className="rp-empty" role="status">
            {t(moment.kind === 'gap' ? GAP_KEY[moment.reason] : 'replay.before')}
          </div>
        )}
      </div>
      {moment.kind === 'screen' && moment.screen.truncated ? (
        <p className="rp-msg">{t('replay.truncated', { count: LIMITS.nodes })}</p>
      ) : null}
      {moment.kind === 'screen' && gap && !BREAKS.has(gap.reason) ? (
        <p className="rp-msg" role="status">
          {t(GAP_KEY[gap.reason])}
        </p>
      ) : null}
      {pending > 0 ? (
        <p className="rp-msg" role="status">
          {t('replay.loadingParts', { count: pending })}
        </p>
      ) : null}
      <div className="rp-ctl">
        <button
          type="button"
          className="btn ghost sm"
          aria-label={t('replay.prevKeyframe')}
          title={`${t('replay.prevKeyframe')} (Page Up)`}
          onClick={() => seekKeyframe(-1)}
        >
          {icon('left')}
        </button>
        <button
          type="button"
          className="btn ghost sm"
          aria-label={playing ? t('replay.pause') : t('replay.play')}
          title={`${playing ? t('replay.pause') : t('replay.play')} (Space)`}
          aria-pressed={playing}
          onClick={() => {
            if (!playing && atRef.current >= timeline.endAt) {
              const k = keyframeNear(timeline, timeline.startAt)
              onSeek(k ?? timeline.startAt)
            }
            setPlaying((p) => !p)
          }}
        >
          {icon(playing ? 'pause' : 'play')}
        </button>
        <button
          type="button"
          className="btn ghost sm"
          aria-label={t('replay.nextKeyframe')}
          title={`${t('replay.nextKeyframe')} (Page Down)`}
          onClick={() => seekKeyframe(1)}
        >
          {icon('right')}
        </button>
        {/* biome-ignore lint/a11y/useSemanticElements: a group of toggle buttons, not a form fieldset */}
        <div className="seg rp-speed" role="group" aria-label={t('replay.speed')}>
          {SPEEDS.map((v) => (
            <button
              key={v}
              type="button"
              aria-pressed={speed === v}
              // Its name starts with what it shows (WCAG 2.5.3): "½× speed", not "one half times".
              aria-label={t('replay.speedValue', { speed: speedText(v) })}
              onClick={() => setSpeed(v)}
            >
              {speedText(v)}
            </button>
          ))}
        </div>
        <span className="rp-time" translate="no">
          <b>{position}</b> / {total}
        </span>
      </div>
      <div className="rp-scrub" ref={trackRef}>
        <div className="rp-rail" aria-hidden="true">
          {pieces.map(([a, b]) => (
            <span
              key={a}
              className="rp-piece"
              style={{ left: pct(a), width: `calc(${pct(b)} - ${pct(a)})` }}
            >
              <i
                style={{
                  width:
                    at <= a
                      ? '0'
                      : at >= b
                        ? '100%'
                        : `${((scale.at(at) - scale.at(a)) / Math.max(1e-9, scale.at(b) - scale.at(a))) * 100}%`,
                }}
              />
            </span>
          ))}
        </div>
        <input
          type="range"
          className="rp-range"
          aria-label={t('replay.position')}
          aria-valuetext={`${position} / ${total}`}
          min={0}
          max={TRACK}
          step={1}
          value={Math.round(scale.at(clamp(at)) * TRACK)}
          onChange={(e) => onSeek(scale.time(Number(e.target.value) / TRACK))}
        />
        {scale.breaks.map((b) => {
          const long = breakLength(i18n, b.to - b.from)
          return (
            <span
              key={`${b.from}-${b.reason}`}
              className="rp-break"
              style={{ left: pct(b.from), width: `calc(${pct(b.to)} - ${pct(b.from)})` }}
              title={t(BREAK_KEY[b.reason] ?? 'replay.break.cut')}
            >
              <span className="rp-break-l">
                <span className="vh">{t(BREAK_KEY[b.reason] ?? 'replay.break.cut')}: </span>
                <span className="full">{t('replay.break.label', { duration: long })}</span>
                <span className="short" aria-hidden="true">
                  {t('replay.break.short', { duration: long })}
                </span>
              </span>
            </span>
          )
        })}
        <div className="rp-ticks">
          {markers
            .filter((m) => m.at >= timeline.startAt && m.at <= timeline.endAt)
            .map((m) =>
              scale.inBreak(m.at) ? (
                <span
                  key={m.id}
                  className={`rp-tick tone-${m.tone} off`}
                  style={{ left: pct(m.at) }}
                  title={t('replay.tickOff', { name: m.name })}
                  aria-hidden="true"
                />
              ) : (
                <button
                  key={m.id}
                  type="button"
                  className={`rp-tick tone-${m.tone}`}
                  style={{ left: pct(m.at) }}
                  title={`${m.name} · ${offset(i18n, m.at - sessionStart)}`}
                  aria-label={t('replay.jump', {
                    name: m.name,
                    at: offset(i18n, m.at - sessionStart),
                  })}
                  onClick={() => onSeek(m.at)}
                />
              ),
            )}
        </div>
      </div>
    </fieldset>
  )
}

/** A break's length as its label says it: minutes from a minute up, else seconds. */
/** A break's length in the clock's words (format.duration), to the minute past one. */
function breakLength(i18n: Translator, ms: number): string {
  return duration(
    i18n,
    ms >= 60_000 ? Math.round(ms / 60_000) * 60_000 : Math.max(1000, Math.round(ms / 1000) * 1000),
  )
}

// --- The wireframe

/** Average glyph width for a size, for masked blocks and wrapping estimates. */
const glyph = (size: number) => size * 0.55

/** One screen's wireframe (the player's, and the live view's). */
export function Wireframe({ screen, label }: { screen: Screen; label: string }) {
  const id = useId().replace(/[^A-Za-z0-9_-]/g, '')
  const { width, height } = screen.viewport
  const drawn = boxes(screen)
  const clips = new Map<string, [number, number, number, number]>()
  for (const b of drawn) if (b.clip) clips.set(b.clip.join(','), b.clip)
  const clipIds = new Map([...clips.keys()].map((k, i) => [k, `${id}-clip-${i}`]))
  return (
    <svg
      className="replay-screen"
      viewBox={`0 0 ${Math.max(1, width)} ${Math.max(1, height)}`}
      role="img"
      aria-label={label}
      preserveAspectRatio="xMidYMid meet"
    >
      <defs>
        <pattern
          id={`${id}-hatch`}
          width="8"
          height="8"
          patternUnits="userSpaceOnUse"
          patternTransform="rotate(45)"
        >
          <rect width="8" height="8" className="rp-hidden-bg" />
          <line x1="0" y1="0" x2="0" y2="8" className="rp-hatch" />
        </pattern>
        {[...clips.entries()].map(([k, [x, y, w, h]]) => (
          <clipPath key={k} id={clipIds.get(k)}>
            <rect x={x} y={y} width={w} height={h} />
          </clipPath>
        ))}
      </defs>
      <rect x={0} y={0} width={width} height={height} className="rp-viewport" />
      {drawn.map((b) => (
        <g
          key={b.id}
          opacity={b.state?.enabled === false ? b.alpha * 0.5 : b.alpha}
          {...(b.clip ? { clipPath: `url(#${clipIds.get(b.clip.join(','))})` } : {})}
        >
          <Shape box={b} hatch={`url(#${id}-hatch)`} />
          {b.text && b.kind !== 'hidden' ? <BoxText box={b} /> : null}
        </g>
      ))}
      {screen.touches.map((touch) => (
        <circle
          key={`${touch.at}-${touch.x}-${touch.y}-${touch.phase}`}
          cx={touch.x}
          cy={touch.y}
          r={touch.phase === 'down' ? 14 : 10}
          className={`rp-touch rp-touch-${touch.phase}`}
        />
      ))}
    </svg>
  )
}

function Shape({ box, hatch }: { box: Box; hatch: string }) {
  const { x, y, width, height } = box
  const radius = box.style?.cornerRadius ?? (box.kind === 'button' || box.kind === 'input' ? 6 : 0)
  if (box.kind === 'hidden')
    return (
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        rx={radius}
        fill={hatch}
        className="rp-hidden"
      />
    )
  const fill = box.style?.background ? rgba(box.style.background) : undefined
  const stroke = box.style?.borderColor ? rgba(box.style.borderColor) : undefined
  const common = {
    x,
    y,
    width,
    height,
    rx: radius,
    ...(fill ? { fill } : {}),
    ...(stroke ? { stroke, strokeWidth: box.style?.borderWidth ?? 1 } : {}),
  }
  switch (box.kind) {
    case 'switch': {
      const on = box.state?.on === true
      const r = height / 2
      return (
        <g>
          <rect {...common} rx={r} className={on ? 'rp-switch-on' : 'rp-switch'} />
          <circle
            cx={on ? x + width - r : x + r}
            cy={y + r}
            r={Math.max(0, r - 2)}
            className="rp-knob"
          />
        </g>
      )
    }
    case 'slider': {
      const v = box.state?.value ?? 0
      return (
        <g>
          <rect x={x} y={y + height / 2 - 2} width={width} height={4} rx={2} className="rp-track" />
          <circle
            cx={x + width * v}
            cy={y + height / 2}
            r={Math.min(height / 2, 10)}
            className="rp-knob"
          />
        </g>
      )
    }
    case 'image':
    case 'video':
    case 'map':
    case 'webview':
      return (
        <g>
          <rect {...common} className={`rp-media rp-${box.kind}`} />
          <line x1={x} y1={y} x2={x + width} y2={y + height} className="rp-media-line" />
          <line x1={x + width} y1={y} x2={x} y2={y + height} className="rp-media-line" />
        </g>
      )
    case 'keyboard':
      return <rect {...common} className="rp-keyboard" />
    case 'button':
    case 'input':
      return (
        <rect
          {...common}
          className={`rp-${box.kind}${box.state?.selected ? ' rp-selected' : ''}`}
        />
      )
    default:
      return <rect {...common} className={fill || stroke ? 'rp-styled' : 'rp-container'} />
  }
}

/** Text, as text nodes; masked text as blocks where its words were, never characters. */
function BoxText({ box }: { box: Box }) {
  const text = box.text
  if (!text) return null
  const size = text.size && text.size > 0 ? Math.min(text.size, 200) : 14
  const lineHeight = size * 1.25
  const color = text.color ? rgba(text.color) : undefined
  const maxLines = text.lines && text.lines > 0 ? text.lines : Number.POSITIVE_INFINITY
  const lines = text.value.split('\n').slice(0, Math.min(maxLines, 200))
  const top = box.y + Math.max(0, (box.height - lines.length * lineHeight) / 2)
  const lineX = (chars: number) => {
    const w = chars * glyph(size)
    if (text.align === 'center') return box.x + (box.width - w) / 2
    if (text.align === 'end') return box.x + box.width - w - 4
    return box.x + 4
  }
  if (text.masked) {
    const runs = maskedRuns(lines.join('\n'))
    return (
      <g className="rp-masked" {...(color ? { fill: color } : {})}>
        {runs.map((r) => {
          const line = lines[r.line] ?? ''
          return (
            <rect
              key={`${r.line}-${r.start}`}
              x={lineX([...line].length) + r.start * glyph(size)}
              y={top + r.line * lineHeight + size * 0.2}
              width={r.length * glyph(size)}
              height={size * 0.7}
              rx={2}
            />
          )
        })}
      </g>
    )
  }
  return (
    <text
      className="rp-text"
      fontSize={size}
      {...(text.weight ? { fontWeight: text.weight } : {})}
      {...(color ? { fill: color } : {})}
    >
      {lines.map((line, i) => (
        // Lines of one text node: their index is their identity.
        // biome-ignore lint/suspicious/noArrayIndexKey: lines have no other key
        <tspan key={i} x={lineX([...line].length)} y={top + (i + 1) * lineHeight - size * 0.25}>
          {line}
        </tspan>
      ))}
    </text>
  )
}
